'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Clock, Globe, Hash, Landmark, MapPin, Minimize2, Percent, Phone, Ruler, Star, Store, Users, X } from '@/components/ui/lucide-memo';
import BrandLocationsMap from '@/components/portal/BrandLocationsMap';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { formatEnumLabel } from '#lib/leasing/portfolio-display-utils';
import { readCampaignRow, resolveCampaignPropertyId } from '#lib/leasing/campaignRow';
import { resolveActiveStoreLocations } from '#lib/brands/activeStoreLocations';

interface StoreEnrichment {
  available: boolean;
  logoUrl?: string | null;
  phone?: string | null; rating?: number | null; ratingCount?: number | null;
  website?: string | null; mapsUri?: string | null; openNow?: boolean | null;
  hours?: string[] | null; hoursText?: string | null; photoUrl?: string | null;
  source?: 'google' | 'osm' | null;
  /** Google Maps has no open shop of the brand here: the store was closed (api/stores/enrich). */
  dropped?: boolean; reason?: string;
  /** Google Maps places the shop elsewhere: the store was moved there. */
  moved?: boolean;
  /** Google Maps lists the shop open: the store is now confirmed. */
  confirmed?: boolean;
}

type StoreVerdictName = 'EXISTS' | 'NOT_FOUND' | 'CLOSED' | 'UNCERTAIN';

type StoreVerdict = {
  verification: { verdict: StoreVerdictName; confidence: number; reason: string; evidence?: string[] };
  applied: 'none' | 'address-corrected' | 'marked-closed' | 'deleted';
  cascade: Array<{ locationId: string; city: string | null; verdict: StoreVerdictName; applied?: 'none' | 'deleted' }>;
  cascadeSkipped: number;
};

// Hex greys/accents only, matching the panel. NOT_FOUND is the one that must stand out.
const VERDICT_COLOR: Record<StoreVerdictName, string> = {
  EXISTS: '#4DAB9A',
  NOT_FOUND: '#BE524B',
  CLOSED: '#C19138',
  UNCERTAIN: '#979A9B',
};

const VERDICT_LABEL: Record<'en' | 'fr', Record<StoreVerdictName, string>> = {
  en: { EXISTS: 'Store exists', NOT_FOUND: 'Not found', CLOSED: 'Permanently closed', UNCERTAIN: 'Could not confirm' },
  fr: { EXISTS: 'Magasin confirmé', NOT_FOUND: 'Introuvable', CLOSED: 'Fermé définitivement', UNCERTAIN: 'Non confirmé' },
};

export type StoreLocation = {
  id?: string; name?: string | null; address?: string | null; city?: string | null;
  brandId?: string;
  postalCode?: string | null; country?: string | null; latitude?: number | null; longitude?: number | null;
  surfaceArea?: number | null; openedAt?: string | null; isClosed?: boolean; closedAt?: string | null;
  externalId?: string | null;
  source?: string | null;
  reviewVerdict?: 'GO' | 'CLOSED' | 'NOGO' | null;
  pendingReview?: boolean;
};

// 14-digit SIRET → its official establishment page on the government registry. For a closed store
// this page shows it as "fermé", real, free proof of closure (a Google Maps link would be wrong,
// closed places are delisted). Falls back to a web search for the closure when there's no SIRET.
const closureProofUrl = (loc: StoreLocation, brandName?: string | null): string => {
  const siret = (loc.externalId ?? '').replace(/[^0-9]/g, '');
  if (siret.length === 14) return `https://annuaire-entreprises.data.gouv.fr/etablissement/${siret}`;
  const q = [brandName, loc.city, loc.postalCode, 'fermeture'].map((s) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean).join(' ');
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
};

/** The asset behind a marker, as much of it as the registry happens to hold. */
type TargetAsset = {
  id: string;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  type?: string | null;
  portfolioName?: string | null;
  location?: { city?: string | null; country?: string | null; resolvedAddress?: string | null } | null;
  thumbnailFileId?: string | null;
  thumbnailFile?: { id?: string } | null;
  urls?: string[];
  totalUnits?: number | null;
  declaredUnitCount?: number | null;
  totalAreaSqm?: number | null;
  occupancyRate?: number | null;
  annualVisitors?: number | null;
  catchmentPopulation?: number | null;
  positioningNote?: string | null;
};

const fmtInt = (n: number, lang: string): string => Math.round(n).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US');

const fmtDate = (iso?: string | null, lang?: string): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'long', year: 'numeric' });
};

// Some closed stores carried a fabricated opening date equal to their closing date. Never present an
// opening date that is the same day as (or after) the closing date, it isn't a real opening.
const sameOrAfter = (openIso?: string | null, closeIso?: string | null): boolean => {
  if (!openIso || !closeIso) return false;
  const o = new Date(openIso).getTime();
  const c = new Date(closeIso).getTime();
  return Number.isFinite(o) && Number.isFinite(c) && o >= c - 24 * 3600 * 1000;
};

/**
 * The brand "Stores" tab: a full map of the brand's stores where every point is clickable. Clicking
 * one selects the nearest store, flies + zooms the camera to it, and opens an info card (address,
 * opening date, closure, surface, maps link). Shared by BrandShortlistDetail and the dev preview.
 */
export const StoresMapPanel = ({
  locations,
  brandName,
  brandLogoUrl,
  lang,
  initialSelectedId,
  brandId,
}: {
  locations: StoreLocation[];
  brandName?: string | null;
  brandLogoUrl?: string | null;
  lang: string;
  /** Preselect a store on mount (dev harness only). Normal usage selects via map click. */
  initialSelectedId?: string;
  /** Owning brand, needed to queue a store for verification. Without it the Verify button hides. */
  brandId?: string;
}) => {
  const { kernel } = useKernel();
  const inventoryLocations = useKernelStore(state => brandId ? state.inventory.Brand.registry[brandId]?.locations : undefined);
  // An applied removal overrides the cached network until that brand receives a new full list.
  // Keep each brand's pending removals when the user switches panels; a failed refresh leaves
  // the old list in place, whereas an authoritative correction or reopening replaces it.
  const [suppressed, setSuppressed] = useState<Record<string, { locations: StoreLocation[] | undefined; ids: string[] }>>({});
  const pendingRemovals = brandId ? suppressed[brandId] : undefined;
  const activeLocations = useMemo(() => resolveActiveStoreLocations<StoreLocation>(locations, inventoryLocations)
    .filter(location => !pendingRemovals || pendingRemovals.locations !== inventoryLocations || !location.id || !pendingRemovals.ids.includes(location.id)),
  [locations, inventoryLocations, pendingRemovals]);
  // THE ASSETS THIS SHORTLIST IS SOURCING FOR. A store map is only meaningful against the centre
  // you are trying to fill: a marker on each target asset is what turns the store cloud into the
  // cannibalisation question the scoring keeps talking about. Read off the OPEN campaign rather
  // than the session subject, because the map belongs to the shortlist the user is reading.
  //
  // Selected as a joined STRING, never an array: this selector runs on every store tick, and a
  // freshly built array is a new identity each time, so the map would re-render on changes that
  // have nothing to do with it. A string compares by value, and useMemo rebuilds the array only
  // when the campaign's targets actually move.
  const targetPropertyIdsKey = useKernelStore((state) => {
    const campaignId = state.sourceSelectedCampaignId;
    if (!campaignId) return '';
    const campaign = readCampaignRow(state.inventory, campaignId);
    if (!campaign) return '';
    // A multi-asset run carries `propertyIds`; a single-asset one only the column.
    const many = Array.isArray(campaign.propertyIds) ? campaign.propertyIds.filter(Boolean) : [];
    if (many.length > 0) return many.join(',');
    return campaign.propertyId ?? '';
  });
  const targetPropertyIds = useMemo(
    () => (targetPropertyIdsKey ? targetPropertyIdsKey.split(',') : []),
    [targetPropertyIdsKey],
  );
  const [selection, setSelection] = useState<{ brandId?: string; id: string } | null>(null);
  const [cityPick, setCityPick] = useState(false);
  // Collapsed by default (owner, 2026-10-03: the open list was too big, worst in Source).
  const [citiesOpen, setCitiesOpen] = useState(false);
  const toggleCities = useCallback(() => setCitiesOpen((open) => !open), []);
  const selectedLocation = selection && selection.brandId === brandId
    ? activeLocations.find(location => location.id === selection.id) ?? null : null;
  const selectionKey = selectedLocation?.id ? JSON.stringify([
    brandId, selectedLocation.id, selectedLocation.name, selectedLocation.address,
    selectedLocation.city, selectedLocation.postalCode, selectedLocation.country,
    selectedLocation.latitude, selectedLocation.longitude,
  ]) : null;
  const requestOwner = useRef<{ key: string | null }>({ key: selectionKey });
  useLayoutEffect(() => {
    requestOwner.current = { key: selectionKey };
  }, [brandId, selectionKey]);
  const [enrichResult, setEnrichResult] = useState<{ key: string; data: StoreEnrichment } | null>(null);
  const [droppedNote, setDroppedNote] = useState<string | null>(null);
  // ONE CARD AT A TIME. Both cards pin to the top centre, so holding a store and an asset open at
  // once would stack them. Selecting either clears the other.
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [assetPhotoBroken, setAssetPhotoBroken] = useState(false);
  // FULL VIEW MOVES THE MAP, IT DOES NOT COVER THE APP. The map is rendered into the Portal's main
  // content area, so the left chat and the right panel stay put and stay usable: the point is to
  // read the map WHILE talking to the agent about it, which a modal would forbid. Null until the
  // node is found, so a surface that has no main area (the dev harness) simply never expands.
  const [expanded, setExpanded] = useState(false);
  const [mainCanvas, setMainCanvas] = useState<HTMLElement | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing when this state settles is a behavioural change, not merge work
    setMainCanvas(document.getElementById('portal-main-canvas'));
  }, []);
  const canExpand = mainCanvas !== null;
  // Escape is the way out of anything that took over the screen, and not offering it is how a
  // surface feels like a trap.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);
  const selectedAsset = useKernelStore((state) =>
    (selectedAssetId ? state.inventory.Property.registry[selectedAssetId] : null) as TargetAsset | null,
  );

  // The map resolves its markers straight out of the Property registry and silently draws NOTHING
  // for an id that is not in it, so an asset outside the org's initial load had no marker at all
  // and no error to explain why. The card reads the same record, so both depend on this.
  useEffect(() => {
    if (targetPropertyIds.length === 0) return;
    void kernel.current?.ensureInventoryIds('Property', targetPropertyIds, { force: false });
  }, [kernel, targetPropertyIds]);

  // A new asset means a new thumbnail to try; without this a single broken image poisoned the
  // banner for every asset selected afterwards.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setAssetPhotoBroken(false);
  }, [selectedAssetId]);
  const enrich = enrichResult?.key === selectionKey ? enrichResult.data : null;
  // A photo URL is only a PROMISE of bytes. The proxy 404s whenever Google will not serve the
  // media, a key without Places, a photo reference that has expired, a store since delisted ,
  // and the card was rendering the <img> anyway, so every one of those became a broken-image
  // icon at the top of the card. Treat a failed load as "no photo" and fall back to the layout
  // that already exists for stores without one.
  const [photoBroken, setPhotoBroken] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verdict, setVerdict] = useState<StoreVerdict | null>(null);

  // Check this store NOW and show what was found. The answer is the point: a button that only
  // said "queued" told the user nothing, which is why this path stopped queueing.
  // Hide stores the server just closed, before the brand's next sync lands.
  const suppress = (requestBrandId: string, removedIds: string[]) => {
    const cachedLocations = kernel.current?.getState().inventory.Brand.registry[requestBrandId]?.locations;
    setSuppressed(previous => ({
      ...previous,
      [requestBrandId]: {
        locations: cachedLocations,
        ids: [...new Set([...(previous[requestBrandId]?.locations === cachedLocations ? previous[requestBrandId]?.ids ?? [] : []), ...removedIds])],
      },
    }));
  };

  const verifyStore = async (locationId: string) => {
    if (!brandId || selectedLocation?.id !== locationId) return;
    const requestBrandId = brandId;
    const owner = { key: selectionKey };
    requestOwner.current = owner;
    const isCurrent = () => requestOwner.current === owner;
    setVerifying(true);
    setVerdict(null);
    try {
      const res = await fetch(
        `/api/inventory/brands/${encodeURIComponent(brandId)}/locations/${encodeURIComponent(locationId)}/verify`,
        { method: 'POST' },
      );
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as StoreVerdict;
      const removedIds = [
        ...(body.applied === 'marked-closed' || body.applied === 'deleted' ? [locationId] : []),
        ...body.cascade.filter(change => change.applied === 'deleted').map(change => change.locationId),
      ];
      if (removedIds.length > 0) suppress(requestBrandId, removedIds);
      if (body.applied !== 'none' || body.cascade.some(change => change.applied && change.applied !== 'none')) {
        await kernel.current?.syncEntity('Brand', requestBrandId, { force: true });
      }
      if (isCurrent()) setVerdict(body);
    } catch {
      if (isCurrent()) setVerdict(null);
    } finally {
      if (isCurrent()) setVerifying(false);
    }
  };

  // Publish the open store so the agent can answer "this store doesn't exist" without being told
  // WHICH store. The selection used to live only here, so the user pointed at a pin and the agent
  // had to guess from 21 candidates. Cleared on unmount so a stale pin never outlives the panel.
  useEffect(() => {
    kernel.current?.setState({ sourceFocusedStoreId: selectedLocation?.id ?? null });
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setVerdict(null);
    setVerifying(false);
    setPhotoBroken(false);
    if (!selectedLocation) setSelection(null);
    else setDroppedNote(null);
  }, [kernel, selectionKey]);
  useEffect(() => () => {
    requestOwner.current = { key: null };
    kernel.current?.setState({ sourceFocusedStoreId: null });
  }, [kernel]);

  useEffect(() => {
    if (!initialSelectedId) return;
    const m = activeLocations.find((l) => l.id === initialSelectedId);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    if (m?.id) setSelection({ brandId, id: m.id });
    // Run once for the given id.
  }, [initialSelectedId, brandId]);

  // Pull the Google Maps store card (phone, hours, rating, website, photo) for an OPEN store when it
  // is selected. Degrades silently when the Places API isn't enabled.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setEnrichResult(null);
    const loc = selectedLocation;
    if (!loc || !loc.id) return;
    let cancelled = false;
    const requestBrandId = brandId;
    const locationId = loc.id;
    fetch(`/api/stores/enrich?locationId=${encodeURIComponent(locationId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j: StoreEnrichment | null) => {
        // A closure or a move is a fact about the brand, whether or not this store is still selected.
        if (j?.dropped && requestBrandId) {
          suppress(requestBrandId, [locationId]);
          setDroppedNote(lang === 'fr' ? `${loc.city ?? 'Ce magasin'} : absent de Google Maps, retiré du réseau` : `${loc.city ?? 'This store'}: not on Google Maps, removed from the network`);
        }
        if ((j?.dropped || j?.moved || j?.confirmed) && requestBrandId) void kernel.current?.syncEntity('Brand', requestBrandId, { force: true });
        if (!cancelled && j && requestOwner.current.key === selectionKey) setEnrichResult({ key: selectionKey!, data: j });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectionKey]);

  // BrandLocationsMap is memo()'d, and this panel re-renders on nine local states (enrich, verdict,
  // verifying, the two photo-broken flags, both selections, expand). An inline
  // arrow in the JSX below would hand the map four fresh identities on every one of those, so the
  // memo never held and a store-card fetch repainted the whole d3 map. Stable callbacks, and the
  // conditional props pass the same reference or undefined.
  const toggleExpand = useCallback(() => setExpanded((on) => !on), []);
  const handleAssetSelect = useCallback((propertyId: string) => {
    setSelection(null);
    setSelectedAssetId(propertyId);
  }, []);
  const handleLocationSelect = useCallback((loc: unknown) => {
    setSelectedAssetId(null);
    setCityPick(false);
    const id = (loc as StoreLocation)?.id;
    if (id && activeLocations.some(location => location.id === id)) setSelection({ brandId, id });
  }, [activeLocations, brandId]);

  // Where the stores are (owner, 2026-10-03): the busiest cities, each a shortcut that flies the map
  // to one of its stores.
  const topCities = useMemo(() => {
    const groups = new Map<string, { label: string; count: number; id?: string }>();
    for (const loc of activeLocations) {
      const city = typeof loc.city === 'string' ? loc.city.trim() : '';
      if (!city) continue;
      const group = groups.get(city.toLowerCase());
      if (group) group.count += 1;
      else groups.set(city.toLowerCase(), { label: city, count: 1, id: loc.id ?? undefined });
    }
    return [...groups.values()].sort((a, b) => b.count - a.count).slice(0, 5);
  }, [activeLocations]);

  const surface = (
    <div className="relative h-full w-full overflow-hidden rounded-[16px]">
      <BrandLocationsMap
        locations={activeLocations as never}
        propertyIds={targetPropertyIds}
        showMapControls
        onToggleExpand={canExpand ? toggleExpand : undefined}
        isExpanded={expanded}
        onAssetSelect={handleAssetSelect}
        selectedAssetId={selectedAssetId}
        isSelectionMode={false}
        brandName={brandName ?? undefined}
        onLocationSelect={handleLocationSelect}
        focusedLocation={selectedLocation as never}
        zoomToFocus={cityPick}
        onCountToggle={topCities.length > 1 ? toggleCities : undefined}
        countOpen={citiesOpen}
      />
      {droppedNote && !selectedLocation && !selectedAsset ? (
        <div className="absolute bottom-3 left-1/2 z-10 max-w-[calc(100%-24px)] -translate-x-1/2 truncate rounded-full border border-white/[0.06] bg-[#303030] px-3 py-1.5 text-[#ECECEC]" style={{ fontSize: 12 }}>
          {droppedNote}
        </div>
      ) : null}
      {citiesOpen && !selectedLocation && !selectedAsset && topCities.length > 1 ? (
        <div className="absolute left-3 top-[44px] z-10 w-[168px] rounded-[12px] border border-[#3A3A3A] bg-[#2A2A2A] p-1">
          {topCities.map((city) => (
            <button
              key={city.label}
              type="button"
              onClick={() => { if (city.id) { setCityPick(true); setCitiesOpen(false); setSelection({ brandId, id: city.id }); } }}
              className="flex h-7 w-full items-center justify-between rounded-[8px] px-2 text-left text-[#ECECEC] transition-colors hover:bg-white/[0.08]"
              style={{ fontSize: 12 }}
            >
              <span className="truncate" style={{ fontSize: 'inherit' }}>{city.label}</span>
              <span className="ml-2 tabular-nums text-[#8E8E8E]" style={{ fontSize: 'inherit' }}>{city.count}</span>
            </button>
          ))}
        </div>
      ) : null}
      {selectedAsset ? (
        // Same slot, same shape and same greys as the store card: an asset and a store are two
        // answers to the same question, so they must not look like two different features.
        <div className="absolute left-1/2 top-3 z-10 w-[280px] max-w-[calc(100%-24px)] max-h-[calc(100%-24px)] -translate-x-1/2 overflow-y-auto rounded-[16px] border border-white/[0.06] bg-[#303030] p-3 shadow-[0_16px_48px_rgba(0,0,0,0.5)]">
          {(() => {
            const thumbId = selectedAsset.thumbnailFileId || selectedAsset.thumbnailFile?.id;
            return thumbId && !assetPhotoBroken ? (
              <div className="relative -mx-3 -mt-3 mb-2">
                { }
                <img
                  src={`/api/inventory/assets/${thumbId}`}
                  alt=""
                  className="h-24 w-full rounded-t-[16px] object-cover"
                  onError={() => setAssetPhotoBroken(true)}
                />
                <button
                  type="button"
                  onClick={() => setSelectedAssetId(null)}
                  className="absolute right-1.5 top-1.5 rounded-full bg-[#303030]/85 p-1 text-[#ECECEC] backdrop-blur hover:bg-[#424242]"
                >
                  <X size={14} />
                </button>
              </div>
            ) : null;
          })()}
          <div className="mb-2 flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-[#ECECEC]" style={{ fontSize: 14 }}>
                {selectedAsset.name?.trim() || (lang === 'fr' ? 'Actif' : 'Asset')}
              </div>
              <span className="flex items-center gap-1 text-[#AFAFAF]">
                <Landmark size={10} className="shrink-0" />
                {/* RETAIL_CENTER reads as shouting, and AGENTS 3.5.1 bans all-caps labels. The
                    canonical formatter renders a missing value as "-", which is not a type: keep
                    the explicit fallback for that case. */}
                {selectedAsset.type?.trim() ? formatEnumLabel(selectedAsset.type) : (lang === 'fr' ? 'Actif ciblé' : 'Target asset')}
                {selectedAsset.portfolioName ? ` · ${selectedAsset.portfolioName}` : ''}
              </span>
            </div>
            {!(selectedAsset.thumbnailFileId || selectedAsset.thumbnailFile?.id) || assetPhotoBroken ? (
              <button type="button" onClick={() => setSelectedAssetId(null)} className="shrink-0 rounded-full p-1 text-[#AFAFAF] hover:bg-white/[0.08] hover:text-white">
                <X size={14} />
              </button>
            ) : null}
          </div>
          <div className="space-y-1.5 leading-snug text-[#AFAFAF]">
            {selectedAsset.positioningNote ? <p className="text-[#ECECEC]">{selectedAsset.positioningNote}</p> : null}
            <div className="flex items-start gap-1.5">
              <MapPin size={12} className="mt-0.5 shrink-0 text-[#8E8E8E]" />
              <span>
                {selectedAsset.location?.resolvedAddress?.trim()
                  || [selectedAsset.address, selectedAsset.location?.city ?? selectedAsset.city, selectedAsset.location?.country ?? selectedAsset.country]
                      .map((s) => (typeof s === 'string' ? s.trim() : s)).filter(Boolean).join(', ')
                  || (lang === 'fr' ? 'Adresse non renseignée' : 'No address on file')}
              </span>
            </div>
            {(() => {
              const units = selectedAsset.declaredUnitCount || selectedAsset.totalUnits;
              return typeof units === 'number' && units > 0 ? (
                <div className="flex items-center gap-1.5">
                  <Hash size={12} className="shrink-0 text-[#8E8E8E]" />
                  <span>{fmtInt(units, lang)} {lang === 'fr' ? 'locaux' : 'units'}</span>
                </div>
              ) : null;
            })()}
            {typeof selectedAsset.totalAreaSqm === 'number' && selectedAsset.totalAreaSqm > 0 ? (
              <div className="flex items-center gap-1.5">
                <Ruler size={12} className="shrink-0 text-[#8E8E8E]" />
                <span>{fmtInt(selectedAsset.totalAreaSqm, lang)} m²</span>
              </div>
            ) : null}
            {typeof selectedAsset.occupancyRate === 'number' ? (
              <div className="flex items-center gap-1.5">
                <Percent size={12} className="shrink-0 text-[#8E8E8E]" />
                <span>
                  {/* The rate is stored as a fraction on some rows and as a percentage on others,
                      and a "0.92% occupied" mall is a nonsense the user would have to decode. */}
                  {fmtInt(selectedAsset.occupancyRate <= 1 ? selectedAsset.occupancyRate * 100 : selectedAsset.occupancyRate, lang)}%
                  {' '}{lang === 'fr' ? 'occupé' : 'occupied'}
                </span>
              </div>
            ) : null}
            {typeof selectedAsset.annualVisitors === 'number' && selectedAsset.annualVisitors > 0 ? (
              <div className="flex items-center gap-1.5">
                <Users size={12} className="shrink-0 text-[#8E8E8E]" />
                <span>{fmtInt(selectedAsset.annualVisitors, lang)} {lang === 'fr' ? 'visiteurs / an' : 'visitors / year'}</span>
              </div>
            ) : null}
            {typeof selectedAsset.catchmentPopulation === 'number' && selectedAsset.catchmentPopulation > 0 ? (
              <div className="flex items-center gap-1.5">
                <Users size={12} className="shrink-0 text-[#8E8E8E]" />
                <span>{fmtInt(selectedAsset.catchmentPopulation, lang)} {lang === 'fr' ? 'hab. dans la zone' : 'in catchment'}</span>
              </div>
            ) : null}
            {selectedAsset.urls?.[0] ? (
              <div className="flex items-center gap-1.5">
                <Globe size={12} className="shrink-0 text-[#8E8E8E]" />
                <a href={selectedAsset.urls[0]} target="_blank" rel="noreferrer" className="truncate text-[#ECECEC] underline decoration-[#555] underline-offset-2 hover:decoration-white">
                  {selectedAsset.urls[0].replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                </a>
              </div>
            ) : null}
            {/* No "open asset" link: an asset has no page to open, and this map is already inside
                that asset's own shortlist, so the only place to send the user is where they are. */}
          </div>
        </div>
      ) : null}
      {selectedLocation ? (
        // Pinned to the top-centre of the map, so the whole card, the store photo first, is always
        // fully visible regardless of the panel's height. The map flies the selected store to the
        // lower-middle of the viewport, so the point sits just below its card like a popup.
        <div
          className="absolute left-1/2 top-3 z-10 w-[280px] max-w-[calc(100%-24px)] max-h-[calc(100%-24px)] -translate-x-1/2 overflow-y-auto rounded-[16px] border border-white/[0.06] bg-[#303030] p-3 shadow-[0_16px_48px_rgba(0,0,0,0.5)]"
        >
          {enrich?.photoUrl && !photoBroken ? (
            // Real photo of the store (Google Places), a full-width banner like a Maps place card.
            // Bleeds to the card edges and rounds the top corners.
            <div className="relative -mx-3 -mt-3 mb-2">
              { }
              <img
                src={enrich.photoUrl}
                alt=""
                className="h-24 w-full rounded-t-[16px] object-cover"
                onError={() => setPhotoBroken(true)}
              />
              <button
                type="button"
                onClick={() => setSelection(null)}
                className="absolute right-1.5 top-1.5 rounded-full bg-[#303030]/85 p-1 text-[#ECECEC] backdrop-blur hover:bg-[#424242]"
              >
                <X size={14} />
              </button>
            </div>
          ) : null}
          <div className="mb-2 flex items-start gap-2">
            {(!enrich?.photoUrl || photoBroken) && (() => {
              const img = enrich?.logoUrl || brandLogoUrl;
              return img ? (
                // Fallback header thumbnail: the brand logo, when there's no store photo.
                 
                <img src={img as string} alt="" className="h-9 w-9 shrink-0 rounded-[8px] bg-white object-contain p-0.5" />
              ) : null;
            })()}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-[#ECECEC]" style={{ fontSize: 14 }}>
                {selectedLocation.name?.trim() || selectedLocation.city?.trim() || brandName || (lang === 'fr' ? 'Point de vente' : 'Store')}
              </div>
              {selectedLocation.pendingReview ? (
                <span className="text-[#AFAFAF]" style={{ fontSize: 12 }}>{lang === 'fr' ? 'À vérifier' : 'Unverified'}</span>
              ) : (
                <span className="text-[#AFAFAF]" style={{ fontSize: 12 }}>{lang === 'fr' ? 'Ouvert' : 'Open'}</span>
              )}
            </div>
            {!enrich?.photoUrl || photoBroken ? (
              <button type="button" onClick={() => setSelection(null)} className="shrink-0 rounded-full p-1 text-[#AFAFAF] hover:bg-white/[0.08] hover:text-white">
                <X size={14} />
              </button>
            ) : null}
          </div>
          <div className="space-y-1.5 leading-snug text-[#AFAFAF]">
            <div className="flex items-start gap-1.5">
              <MapPin size={12} className="mt-0.5 shrink-0 text-[#8E8E8E]" />
              <span>
                {[selectedLocation.address, [selectedLocation.postalCode, selectedLocation.city].filter(Boolean).join(' '), selectedLocation.country]
                  .map((s) => (typeof s === 'string' ? s.trim() : s)).filter(Boolean).join(', ') || (lang === 'fr' ? 'Adresse non renseignée' : 'No address on file')}
              </span>
            </div>
            {fmtDate(selectedLocation.openedAt, lang) && !sameOrAfter(selectedLocation.openedAt, selectedLocation.closedAt) ? (
              <div className="flex items-center gap-1.5">
                <Calendar size={12} className="shrink-0 text-[#8E8E8E]" />
                <span>{lang === 'fr' ? 'Ouvert le' : 'Opened'} {fmtDate(selectedLocation.openedAt, lang)}</span>
              </div>
            ) : null}
            {selectedLocation.isClosed && fmtDate(selectedLocation.closedAt, lang) ? (
              <div className="flex items-center gap-1.5">
                <Calendar size={12} className="shrink-0 text-[#F28B82]" />
                <span className="text-[#F28B82]">{lang === 'fr' ? 'Fermé le' : 'Closed'} {fmtDate(selectedLocation.closedAt, lang)}</span>
              </div>
            ) : null}
            {typeof selectedLocation.surfaceArea === 'number' && selectedLocation.surfaceArea > 0 ? (
              <div className="flex items-center gap-1.5">
                <Store size={12} className="shrink-0 text-[#8E8E8E]" />
                <span>{Math.round(selectedLocation.surfaceArea).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US')} m²</span>
              </div>
            ) : null}
            {/* Live Google Maps store-card data (open stores, when the Places API is enabled). */}
            {enrich?.phone ? (
              <div className="flex items-center gap-1.5">
                <Phone size={12} className="shrink-0 text-[#8E8E8E]" />
                <a href={`tel:${enrich.phone.replace(/\s/g, '')}`} className="hover:underline">{enrich.phone}</a>
              </div>
            ) : null}
            {typeof enrich?.rating === 'number' ? (
              <div className="flex items-center gap-1.5">
                <Star size={12} className="shrink-0 text-[#8E8E8E]" />
                <span>{enrich.rating.toFixed(1)}{enrich.ratingCount ? ` · ${enrich.ratingCount.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US')} ${lang === 'fr' ? 'avis' : 'reviews'}` : ''}</span>
              </div>
            ) : null}
            {enrich?.hours && enrich.hours.length >= 7 ? (
              <div className="flex items-start gap-1.5">
                <Clock size={12} className="mt-0.5 shrink-0 text-[#8E8E8E]" />
                <span>{enrich.hours[(new Date().getDay() + 6) % 7]}</span>
              </div>
            ) : enrich?.hoursText ? (
              // OSM carries a compact weekly spec (e.g. "Mo-Sa 09:00-20:00; Su 10:00-19:00"), not a
              // per-day list, show it as-is so the hours are complete and honest.
              <div className="flex items-start gap-1.5">
                <Clock size={12} className="mt-0.5 shrink-0 text-[#8E8E8E]" />
                <span className="break-words">{enrich.hoursText}</span>
              </div>
            ) : null}
            {enrich?.website ? (
              <div className="flex items-center gap-1.5">
                <Globe size={12} className="shrink-0 text-[#8E8E8E]" />
                <a href={enrich.website} target="_blank" rel="noreferrer" className="truncate text-[#ECECEC] underline decoration-[#555] underline-offset-2 hover:decoration-white">
                  {enrich.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                </a>
              </div>
            ) : null}
            {selectedLocation.isClosed ? (
              // Closed stores are delisted from Google Maps, so a Maps link is meaningless. Link the
              // OFFICIAL proof of closure instead: the establishment's government-registry page.
              <a
                href={closureProofUrl(selectedLocation, brandName)}
                target="_blank" rel="noreferrer"
                className="mt-1 inline-block text-[11px] text-[#ECECEC] underline decoration-[#555] underline-offset-2 hover:decoration-white"
              >
                {lang === 'fr' ? 'Preuve de fermeture (registre officiel) ↗' : 'Proof of closure (official registry) ↗'}
              </a>
            ) : (() => {
              // Open store → the actual place on Google Maps (search by brand + address so it
              // resolves to the store's listing, not a bare coordinate pin).
              const q = [brandName, selectedLocation.address, selectedLocation.postalCode, selectedLocation.city, selectedLocation.country]
                .map((s) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean).join(' ');
              const href = q
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
                : (selectedLocation.latitude != null && selectedLocation.longitude != null
                    ? `https://www.google.com/maps/search/?api=1&query=${selectedLocation.latitude},${selectedLocation.longitude}`
                    : null);
              return href ? (
                <a href={href} target="_blank" rel="noreferrer" className="mt-1 inline-block text-[11px] text-[#ECECEC] underline decoration-[#555] underline-offset-2 hover:decoration-white">
                  {lang === 'fr' ? 'Voir sur Google Maps ↗' : 'View on Google Maps ↗'}
                </a>
              ) : null;
            })()}
            {/* Does this store actually exist? Some of these rows were written by a model with no
                existence check, so the Maps link above proves nothing, it text-searches and will
                happily show something near a fabricated address. This queues the real checker. */}
            {selectedLocation.id && brandId ? (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => void verifyStore(selectedLocation.id as string)}
                  disabled={verifying}
                  className="block h-8 rounded-full border border-[#444] px-3 text-[#ECECEC] transition-colors hover:bg-white/[0.08] disabled:opacity-50"
                  style={{ fontSize: 13 }}
                >
                  {verifying
                    ? (lang === 'fr' ? 'Vérification…' : 'Verifying…')
                    : (lang === 'fr' ? 'Vérifier' : 'Verify')}
                </button>
                {verdict ? (
                  <div className="mt-2 rounded-[12px] border border-white/[0.06] bg-[#262626] p-2">
                    <div className="font-semibold" style={{ fontSize: 11, color: VERDICT_COLOR[verdict.verification.verdict] }}>
                      {VERDICT_LABEL[lang === 'fr' ? 'fr' : 'en'][verdict.verification.verdict]}
                      {' · '}
                      {Math.round((verdict.verification.confidence ?? 0) * 100)}%
                    </div>
                    <div className="mt-1 text-white/70" style={{ fontSize: 10 }}>{verdict.verification.reason}</div>
                    {/* The evidence is the whole point: a verdict you cannot argue with is just
                        another confident guess, and these rows already came from one. */}
                    {(verdict.verification.evidence ?? []).slice(0, 3).map((line, index) => (
                      <div key={index} className="mt-1 text-white/45" style={{ fontSize: 10 }}>{line}</div>
                    ))}
                    {verdict.applied !== 'none' ? (
                      <div className="mt-1 text-[#ECECEC]" style={{ fontSize: 10 }}>
                        {verdict.applied === 'address-corrected'
                          ? (lang === 'fr' ? 'Adresse corrigée.' : 'Address corrected.')
                          : verdict.applied === 'deleted'
                            ? (lang === 'fr' ? 'Magasin supprimé (sauvegardé pour annulation).' : 'Store deleted (snapshotted so it can be restored).')
                            : (lang === 'fr' ? 'Marqué comme fermé.' : 'Marked as closed.')}
                      </div>
                    ) : null}
                    {verdict.cascade.length > 0 ? (
                      <div className="mt-1 text-white/45" style={{ fontSize: 10 }}>
                        {lang === 'fr'
                          ? `${verdict.cascade.length} autres magasins vérifiés (${verdict.cascade.filter((c) => c.verdict === 'NOT_FOUND').length} introuvables, ${verdict.cascade.filter((c) => c.applied === 'deleted').length} supprimés)`
                          : `${verdict.cascade.length} other stores checked (${verdict.cascade.filter((c) => c.verdict === 'NOT_FOUND').length} not found, ${verdict.cascade.filter((c) => c.applied === 'deleted').length} deleted)`}
                        {verdict.cascadeSkipped > 0
                          ? (lang === 'fr' ? `, ${verdict.cascadeSkipped} non vérifiés` : `, ${verdict.cascadeSkipped} not checked`)
                          : ''}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );

  if (!expanded || !mainCanvas) return surface;

  return (
    <>
      {/* The tab keeps its height while the map is away, so nothing jumps when it comes back. It
          sits UNDER the expanded map and is only seen on the way out, which is why the way home is
          the map's own Exit full view control and Escape, not this. It is still a button because a
          thing that says "click to bring it back" and is not clickable is a lie. */}
      <button
        type="button"
        onClick={() => setExpanded(false)}
        className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-[16px] border border-[#262626] bg-[#1B1B1B] text-[#8E8E8E] transition-colors hover:bg-[#222] hover:text-[#ECECEC]"
      >
        <Minimize2 size={14} />
        {lang === 'fr' ? 'Carte en vue complète. Cliquez pour la ramener ici.' : 'Map is in full view. Click to bring it back here.'}
      </button>
      {createPortal(
        <div className="absolute inset-0 z-30 overflow-hidden bg-[#141414]">{surface}</div>,
        mainCanvas,
      )}
    </>
  );
};

StoresMapPanel.displayName = 'StoresMapPanel';
